"use client";

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { SlOptionsVertical } from "react-icons/sl";
import { FaLink } from "react-icons/fa";
import { MdMore } from "react-icons/md";
import { usePathname } from "next/navigation";
import Link from "next/link";

import { useMessage } from "../providers/MessageProvider";
import envConfig from "@/config/envConfig";
import { useExclusiveDropdown, usePortalDropdown } from "@/hooks";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";

interface NewsMoreOptionProps {
  url: string;
  moreUrl: string;
  /** "up" opens dropdown above button (for cards at bottom of container) */
  dropdownPosition?: "up" | "down";
  /** Use portal to render dropdown in body (avoids overflow clipping) */
  usePortal?: boolean;
}

export default function NewsMoreOption({
  url,
  moreUrl,
  dropdownPosition = "down",
  usePortal = true,
}: NewsMoreOptionProps) {
  const { isOpen: isDropdownOpen, toggleDropdown, closeDropdown } = useExclusiveDropdown();
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const language = locale?.split("-")[0] || "en";

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { showMessage } = useMessage();

  const { dropdownStyle, isPositionReady } = usePortalDropdown({
    isOpen: isDropdownOpen,
    usePortal,
    triggerRef,
    dropdownPosition,
    estimatedHeight: 120,
    onClose: closeDropdown,
  });

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      closeDropdown();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen, closeDropdown]);

  const handleCopyLink = () => {
    const sharePath = url.replace(/^\/(te-IN|en-IN)(?=\/|$)/i, "") || "/";
    navigator.clipboard.writeText(`${envConfig?.clientUrl}${sharePath}`);
    showMessage(
      "info",
      language === "te" ? "లింక్ క్లిప్‌బోర్డ్‌కు కాపీ చేయబడింది!" : "Link copied to clipboard!",
    );
    closeDropdown();
  };

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={`white shadow-md z-[9999] bg-white dark:bg-gray-800 dark:text-white min-w-[180px] py-3 rounded ${
        !usePortal
          ? dropdownPosition === "up"
            ? "absolute right-0 bottom-[100%] mb-2"
            : "absolute right-0 top-[100%] mt-1"
          : ""
      }`}
      style={usePortal ? dropdownStyle : undefined}
    >
      <Link href={moreUrl} onClick={closeDropdown}>
        <div className="flex items-center gap-2 cursor-pointer px-4 py-2 transition-colors hover:text-[#0055a5]">
          <span className="flex items-center justify-center">
            <MdMore />
          </span>
          <span className="text-sm font-medium whitespace-nowrap">
            {language === "te" ? "మరిన్ని వార్తలు" : "More News"}
          </span>
        </div>
      </Link>
      <div
        className="flex items-center gap-2 cursor-pointer px-4 py-2 transition-colors hover:text-[#0055a5]"
        onClick={handleCopyLink}
      >
        <span className="flex items-center justify-center">
          <FaLink />
        </span>
        <span className="text-sm font-medium whitespace-nowrap">
          {language === "te" ? "లింక్ కాపీ చేయండి" : "Copy Link"}
        </span>
      </div>
    </div>
  );

  const shouldRenderDropdown = isDropdownOpen && (!usePortal || isPositionReady);

  return (
    <div className="relative inline-flex items-center justify-center">
      <button
        ref={triggerRef}
        type="button"
        className="inline-flex size-6 shrink-0 items-center justify-center text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
        onClick={toggleDropdown}
        aria-label="More Options"
      >
        <SlOptionsVertical size={16} className="text-gray-500 dark:text-gray-300" />
      </button>
      {shouldRenderDropdown &&
        (usePortal && typeof document !== "undefined"
          ? createPortal(dropdownContent, document.body)
          : dropdownContent)}
    </div>
  );
}
